import type { AppState } from '@/store/app.state';
import type { Language } from '@/utils/i18n/language.interface';
import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { firstValueFrom, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdvizeoCoreReferenceListsService } from './advizeo-core-reference-lists.service';
import { allReferenceListsNames, type ReferenceListItem, type ReferenceListName } from './reference-lists.interface';
import * as ReferenceListsStoreActions from './store/reference-lists.actions';

type ReferenceLists = Partial<Record<ReferenceListName, Record<string, ReferenceListItem>>>;

@Injectable({
    providedIn: 'root'
})
export class ReferenceListsService {
    private _advizeoCoreReferenceListsService = inject(AdvizeoCoreReferenceListsService);

    private _store = inject<Store<AppState>>(Store);

    private _referenceLists = signal<ReferenceLists | null>(null);
    private _referenceLists$: Observable<ReferenceLists | null> = toObservable(this._referenceLists);

    fetchReferenceLists(lang: Language, listNames: ReferenceListName[] = allReferenceListsNames): void {
        firstValueFrom(this._advizeoCoreReferenceListsService.fetchReferenceLists$(lang, listNames))
            .then(lists => {
                this._referenceLists.set(
                    Object.entries(lists as Record<string, ReferenceListItem[]>).reduce(
                        (acc, [listName, listItems]) => {
                            acc[listName as ReferenceListName] = listItems.reduce(
                                (accItems, listItem) => {
                                    accItems[listItem.code] = listItem;
                                    return accItems;
                                },
                                {} as Record<string, ReferenceListItem>
                            );
                            return acc;
                        },
                        {} as Partial<Record<ReferenceListName, Record<string, ReferenceListItem>>>
                    )
                );
                this._store.dispatch(new ReferenceListsStoreActions.FetchReferenceListsSucceeded(lists));
            })
            .catch(error => {
                console.error(`something went wrong while fetching reference lists: ${lang} : ${listNames.join(',')}::`, error);
            });
    }

    getReferenceList$(listName: ReferenceListName): Observable<ReferenceListItem[]> {
        return this._referenceLists$.pipe(map(lists => Object.values(lists?.[listName] ?? {})));
    }

    getReferenceListR(listName: ReferenceListName): ReferenceListItem[] {
        return Object.values(this._referenceLists()?.[listName] ?? {});
    }

    getReferenceListItemCodes$(listName: ReferenceListName): Observable<string[]> {
        return this._referenceLists$.pipe(map(lists => Object.keys(lists?.[listName] ?? {})));
    }

    getReferenceListItem$(listName: ReferenceListName, itemCode: string): Observable<ReferenceListItem> {
        return this._referenceLists$.pipe(map(lists => lists?.[listName]?.[itemCode] ?? { code: itemCode, label: itemCode }));
    }

    getReferenceListItemR(listName: ReferenceListName, itemCode: string): ReferenceListItem {
        return this._referenceLists()?.[listName]?.[itemCode] ?? { code: itemCode, label: itemCode };
    }

    getReferenceListItemLabel$(listName: ReferenceListName, itemCode: string): Observable<string> {
        return this.getReferenceListItem$(listName, itemCode).pipe(map(item => item.label));
    }

    getReferenceListItemLabelR(listName: ReferenceListName, itemCode: string): string {
        return this.getReferenceListItemR(listName, itemCode).label;
    }
}
